import { createContext, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";

export type NavigationConfig<TItem, TQuery> = {
  // Function that fetches items for a given query.
  // Note that this expects a DRF-style response.
  fetchItems: (query: TQuery) => Promise<{ results: TItem[] }>;

  // Get the unique ID of an item, e.g. (thread) => thread.id
  getItemId: (item: TItem) => string;

  // Generate a URL for the page that shows a specific item, e.g. (threadId) => `/messages/${threadId}`
  buildItemUrl: (itemId: string) => string;

  // Return the query parameters for fetching the next/previous page of items,
  // or undefined if there is no next/previous page
  getNextPageQuery: (
    query: TQuery,
    currentPageSize: number,
    totalCount: number
  ) => TQuery | undefined;

  getPrevPageQuery: (query: TQuery) => TQuery | undefined;
};

export const defaultDRFQueryFunctions = {
  // Provides default implementations of getNextPageQuery and getPrevPageQuery
  // for DRF-style limit/offset pagination.
  // You can spread them into your NavigationConfig to use them.
  getNextPageQuery: <TQuery extends { offset: number; limit: number }>(
    query: TQuery,
    currentPageSize: number,
    totalCount: number
  ): TQuery | undefined => {
    const hasNextPage = query.offset + currentPageSize < totalCount;
    if (!hasNextPage) return undefined;

    return {
      ...query,
      offset: query.offset + query.limit,
    };
  },

  getPrevPageQuery: <TQuery extends { offset: number; limit: number }>(
    query: TQuery
  ): TQuery | undefined => {
    const hasPrevPage = query.offset > 0;
    if (!hasPrevPage) return undefined;

    return {
      ...query,
      offset: Math.max(0, query.offset - query.limit),
    };
  },
};

type NavigationContext<TQuery> = {
  onPrev?: () => void;
  onNext?: () => void;
  query: TQuery;
  count: number;
  currentPosition: number;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const NavigationContext = createContext<NavigationContext<any>>({} as any);

export function useNavigationContext<TQuery>() {
  return useContext(NavigationContext) as NavigationContext<TQuery>;
}

export function NavigationContextProvider<TItem, TQuery>({
  itemId,
  items,
  query,
  count,
  config,
  children,
}: React.PropsWithChildren<{
  itemId: string;
  items: TItem[];
  query: TQuery;
  count: number;
  config: NavigationConfig<TItem, TQuery>;
}>) {
  const navigate = useNavigate();

  const value: NavigationContext<TQuery> = useMemo(() => {
    if (!query) {
      return {
        onNext: undefined,
        onPrev: undefined,
        query,
        count: 0,
        currentPosition: 0,
      };
    }

    const currentIndex = items.findIndex((item) => config.getItemId(item) === itemId);
    const offset = (query as { offset?: number })?.offset ?? 0;
    const currentPosition = currentIndex >= 0 ? offset + currentIndex + 1 : 0;

    function getOnPrev() {
      // There's a previous item in the current list
      if (currentIndex > 0) {
        return () => {
          const prevItem = items[currentIndex - 1];
          const prevItemId = config.getItemId(prevItem);
          navigate(config.buildItemUrl(prevItemId), { state: { query } });
        };
      }

      // There's a previous page of items
      const prevPageQuery = config.getPrevPageQuery(query);
      if (prevPageQuery) {
        return async () => {
          const { results: prevItems } = await config.fetchItems(prevPageQuery);
          const prevItem = prevItems[prevItems.length - 1];
          if (prevItem) {
            const prevItemId = config.getItemId(prevItem);
            navigate(config.buildItemUrl(prevItemId), { state: { query: prevPageQuery } });
          }
        };
      }

      // No previous items
      return undefined;
    }

    function getOnNext() {
      // There's a next item in the current list
      if (currentIndex < items.length - 1) {
        return () => {
          const nextItem = items[currentIndex + 1];
          const nextItemId = config.getItemId(nextItem);
          navigate(config.buildItemUrl(nextItemId), { state: { query } });
        };
      }

      // There's another page of items
      const nextPageQuery = config.getNextPageQuery(query, items.length, count);
      if (nextPageQuery) {
        return async () => {
          const { results: nextItems } = await config.fetchItems(nextPageQuery);
          const nextItem = nextItems[0];
          if (nextItem) {
            const nextItemId = config.getItemId(nextItem);
            navigate(config.buildItemUrl(nextItemId), { state: { query: nextPageQuery } });
          }
        };
      }

      // No more items
      return undefined;
    }

    const onPrev = getOnPrev();
    const onNext = getOnNext();

    return { onPrev, onNext, query, count, currentPosition };
  }, [config, count, itemId, items, navigate, query]);

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}
